import { FilterValues } from "interfaces/filter/filter";
import { TableData } from "interfaces/table/TableInterface";
import { QueryParam } from "interfaces/UserInterface";
import {
  PORTFOLIO_COMPANY_ROUTE,
  PORTFOLIO_MERCHANT_ROUTE,
  PORTFOLIO_TRENDS_VOLUME_ROUTE,
} from "constants/routes/ApiRoutes";
import { httpService } from "services/HttpService";
import { dateUtils } from "utils/common/dateUtils";
import { PortfolioResponse, PortfolioTrendsItem } from "components/molecules/portfolio/PortfolioTrendsSection";
import { PortfolioTopBottomItem } from "components/molecules/portfolio/PortfolioTopSection";

export interface TopMerchantResponse {
  item: TopMerchantItem[];
  total?: number;
}
export type SortDirection = "ASC" | "DESC";
export interface Sort {
  by: string;
  dir: SortDirection;
}

interface TopMerchantItem extends TableData, AccountIdAndName, CompanyIdAndName, MerchantIdAndName {
  docCount: number; // Integer
  transactionSuccessRate: number; // Double
  numberTransaction: number; // Double
  grossAmount: number; // Double
}

export interface TopMerchantsFilter extends AccountCompanyMerchantIds {
  minAcceptationDatetime: Date; // Datetime
  maxAcceptationDatetime: Date; // Datetime
  sort: Sort;
}

// export function fetchTopMerchants(
//   filter: TopMerchantsFilter,
// ): Promise<[TopMerchantResponse, TopMerchantResponse]> {
//   return Promise.all<TopMerchantResponse, TopMerchantResponse>([
//     httpService.get<TopMerchantResponse>(buildTopMerchantsUrl(filter, "ASC")),
//     httpService.get<TopMerchantResponse>(buildTopMerchantsUrl(filter, "DESC")),
//   ]);
// }

/**
 * Overwrites given filters sort direction with provided one
 * @param filter filters to clone with replaced sort direction
 * @param sort sort direction to replace one in the filters
 */
// function buildTopMerchantsUrl(
//   filter: TopMerchantsFilter,
//   sort: Sort,
// ): string {
//   return buildUrl(
//     "/portfolio/top-merchants",
//     topMerchantsFilterStrategies,
//     buildSortQueryParamStrategy(
//       "sortBy",
//       "sortDirection",
//     ),
//     filter,
//     sort,
//   );
// }
//
// const topMerchantsFilterStrategies: readonly QueryParamStrategy<TopMerchantsFilter>[] = [
//   buildQueryMultiParamStrategy(
//     "accountId",
//     (filter) => filter.accountIds,
//     convertLongIdToString
//   ),
//   buildQueryMultiParamStrategy(
//     "companyId",
//     (filter) => filter.companyIds,
//     convertLongIdToString
//   ),
//   buildQueryMultiParamStrategy(
//     "merchantId",
//     (filter) => filter.merchantIds,
//     convertLongIdToString
//   ),
//   buildQueryParamStrategy(
//     "minAcceptationDatetime",
//     (filter) => filter.minAcceptationDatetime,
//     convertDatetimeToIsoString
//   ),
//   buildQueryParamStrategy(
//     "maxAcceptationDatetime",
//     (filter) => filter.maxAcceptationDatetime,
//     convertDatetimeToIsoString
//   ),
// ];

//
// COMMON INTERFACES
//

interface AccountIdAndName {
  accountId: number;
  accountName: string;
}

interface CompanyIdAndName {
  companyId: number;
  companyName: string;
}

interface MerchantIdAndName {
  merchantId: number;
  merchantName: string;
}

interface AccountCompanyMerchantIds {
  accountIds: readonly number[]; // Long[]
  companyIds: readonly number[]; // Long[]
  merchantIds: readonly number[]; // Long[]
}

const computeFiltersTrends = (defaultFilters: FilterValues): Record<string, string> => {
  return {
    accountId: defaultFilters.accounts.map((account) => account.id).toString(),
    companyId: defaultFilters.companies.selectedOptions.map((company) => company.id).toString(),
    merchantId: defaultFilters.merchants.selectedOptions.map((merchant) => merchant.id).toString(),
  };
};

const computeFiltersTopBottom = (filters: FilterValues, companyMode: boolean) => {
  return {
    accountId: filters.accounts.map((account) => account.id).toString(),
    companyId: filters.companies.selectedOptions.map((company) => company.id).toString(),
    merchantId: companyMode ? "" : filters.merchants.selectedOptions.map((merchant) => merchant.id).toString(),
    minAcceptationDatetime: dateUtils.getDateTimeFormat(filters.operationDate.startDate),
    maxAcceptationDatetime: dateUtils.getDateTimeFormat(filters.operationDate.endDate),
  };
};

const buildFiltersTrends = (defaultFilters: FilterValues) => {
  const searchParams: Record<string, string> = computeFiltersTrends(defaultFilters);
  const params = new URLSearchParams({ ...searchParams });
  [...params.entries()].forEach(([key, value]) => {
    if (value === undefined || value === "") {
      params.delete(key);
    }
  });

  return params.toString() ? `${params.toString()}&` : "";
};
const buildFiltersTopBottom = (defaultFilters: FilterValues, companyMode: boolean) => {
  const searchParams: Record<string, string> = computeFiltersTopBottom(defaultFilters, companyMode);
  const params = new URLSearchParams({ ...searchParams });
  [...params.entries()].forEach(([key, value]) => {
    if (value === undefined || value === "") {
      params.delete(key);
    }
  });

  return params.toString() ? `${params.toString()}&` : "";
};

const queryParamToPageable = ({
  page = undefined,
  pageSize = 50,
  sortDirection = "ASC",
  sortField = "rateAmountEvolutionDay",
}: QueryParam): string => {
  const pageParam = page ? `&page=${page}` : "";
  const pageSizeParam = pageSize ? `pageSize=${pageSize}` : "";

  return `${pageSizeParam + pageParam}&sortBy=${sortField}&sortDirection=${sortDirection}`;
};

const getUrlPortfolioTrends = (defaultFilters: FilterValues, searchParams: QueryParam): string => {
  return `?${buildFiltersTrends(defaultFilters)}${queryParamToPageable(searchParams)}`;
};

const getUrlPortfolioTopBottom = (
  defaultFilters: FilterValues,
  searchParams: QueryParam,
  companyMode: boolean
): string => {
  return `?${buildFiltersTopBottom(defaultFilters, companyMode)}${queryParamToPageable(searchParams)}`;
};

export const getPortfolioTrends =
  (defaultFilters: FilterValues, options?: RequestInit) =>
  (searchParams: QueryParam): Promise<PortfolioResponse<PortfolioTrendsItem>> => {
    const url = PORTFOLIO_TRENDS_VOLUME_ROUTE + getUrlPortfolioTrends(defaultFilters, searchParams);
    return httpService.get(url, options);
  };

export const getPortfolioTopBottom =
  (defaultFilters: FilterValues, companyMode: boolean, options?: RequestInit) =>
  (searchParams: QueryParam): Promise<PortfolioResponse<PortfolioTopBottomItem>> => {
    
    const apiRoute = companyMode ? PORTFOLIO_COMPANY_ROUTE : PORTFOLIO_MERCHANT_ROUTE;

    const url = apiRoute + getUrlPortfolioTopBottom(defaultFilters, searchParams, companyMode);

    return httpService.get(url, options);
  };
